import { Region, SeatsioEventManager } from '@seatsio/seatsio-react';
import { SectionTitle } from 'features/common/components/SectionTitle';
import { Divider } from 'features/common/components/Divider';
import useGetEvent from 'features/common/hooks/useGetEvent';
import useGetPartnerData from 'features/common/hooks/useGetPartnerData';
import { useResponsiveScreen } from 'hooks/useWindowSize';
import { useState } from 'react';
import { getData } from 'services/api';
import { useToastNotification } from 'hooks/useToastNotification';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as Styled from './SeatsSection.styles';
import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';

type SeatsMode =
	| 'manageObjectStatuses'
	| 'manageCategories'
	| 'manageForSaleConfig'
	| 'manageTableBooking'
	| 'manageChannels'
	| 'select'
	| 'static';

const SEAT_MODES = [
	{ value: 'manageObjectStatuses', label: 'Manage Object Statuses' },
	{ value: 'manageCategories', label: 'Manage Categories' },
	{ value: 'manageForSaleConfig', label: 'Manage For Sale Config' },
	{ value: 'manageTableBooking', label: 'Manage Table Booking' },
	{ value: 'manageChannels', label: 'Manage Channels' },
	{ value: 'select', label: 'Select Mode' },
	{ value: 'static', label: 'Static View (Read-only)' },
] as const;

interface Channel {
	key: string;
	name: string;
	color?: string;
	objects?: string[];
}

export const SeatsSection = () => {
	const { event } = useGetEvent();
	const { partnerData } = useGetPartnerData();
	const isLargeScreen = useResponsiveScreen();
	const [mode, setMode] = useState<SeatsMode>('manageObjectStatuses');
	const { t } = useTranslation();
	const { showNotification, TYPES } = useToastNotification();

	// Fetch seats.io event data including channels
	const { data: seatsEventData } = useQuery({
		queryKey: ['seats-event', event?.seatsEventKey],
		queryFn: async () => {
			if (!event?.seatsEventKey) return null;
			try {
				const data = await getData(`/seats/events/${event.seatsEventKey}`);
				return data;
			} catch (error) {
				// Error fetching seats event
				return null;
			}
		},
		enabled: !!event?.seatsEventKey,
	});

	const eventChannels = (seatsEventData?.channels || []) as Channel[];

	if (!event?.seatsEventKey || !partnerData?.seats_io_secret_key) {
		return null;
	}

	const secretKey = partnerData?.seats_io_secret_key;
	const region = (partnerData?.seats_region || 'na') as Region;
	const eventKey = event.seatsEventKey;
	const widgetUrl = process.env.REACT_APP_WIDGET;

	const handleCopyLink = (channelId: string) => {
		const link = `${widgetUrl}/event/${event.id}/tickets?compCode=${channelId}`;
		if (window.parent !== window) {
			window.parent.postMessage(
				{
					action: 'copy-to-clipboard',
					text: link,
				},
				'*'
			);
		} else {
			navigator.clipboard
				.writeText(link)
				.then(() => {
					showNotification({
						type: TYPES.SUCCESS,
						message: t('copyToClipboard-success', { content: t('seats-channel-links-title') }),
					});
				})
				.catch(() => {
					showNotification({
						type: TYPES.ERROR,
						message: t('copyToClipboard-error'),
					});
				});
		}
	};

	return (
		<>
			<Styled.HeaderRow>
				<SectionTitle title={t('seats-section-title')} />
				{isLargeScreen && (
					<Styled.ModeSelector value={mode} onChange={(e) => setMode(e.target.value as SeatsMode)}>
						{SEAT_MODES.map((m) => (
							<option key={m.value} value={m.value}>
								{m.label}
							</option>
						))}
					</Styled.ModeSelector>
				)}
			</Styled.HeaderRow>

			<Divider />

			{/* Channel Links Section */}
			{eventChannels.length > 0 && (
				<Styled.ChannelLinksSection>
					<Styled.ChannelLinksTitle>{t('seats-channel-links-title')}</Styled.ChannelLinksTitle>
					<Styled.ChannelDescription>{t('seats-channel-links-description')}</Styled.ChannelDescription>
					<Styled.ChannelList>
						{eventChannels.map((channel) => {
							const link = `${widgetUrl}/event/${event.id}/tickets?compCode=${channel.key}`;
							return (
								<Styled.ChannelItem key={channel.key}>
									<Styled.ChannelInfo>
										<Styled.ChannelName>{t('seats-channel-name', { name: channel.name })}</Styled.ChannelName>
										<Styled.ChannelKey>{t('seats-channel-key', { key: channel.key })}</Styled.ChannelKey>
									</Styled.ChannelInfo>
									<Styled.ChannelLink>{link}</Styled.ChannelLink>
									<Styled.ButtonWrapper>
										<PunchupButton text={t('seats-copy-link')} onClick={() => handleCopyLink(channel.key)} />
									</Styled.ButtonWrapper>
								</Styled.ChannelItem>
							);
						})}
					</Styled.ChannelList>
				</Styled.ChannelLinksSection>
			)}
			{eventChannels.length === 0 && seatsEventData && (
				<Styled.ChannelLinksSection>
					<Styled.NoChannelsMessage>{t('seats-no-channels')}</Styled.NoChannelsMessage>
				</Styled.ChannelLinksSection>
			)}

			<Styled.SeatsContainer>
				<SeatsioEventManager secretKey={secretKey} event={eventKey} region={region} mode={mode} />
			</Styled.SeatsContainer>
		</>
	);
};
